import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Input, Spin, SelectProps, Select, InputNumber, App, Upload, TreeSelect, Switch, Divider, TimePicker, Space, Typography } from "antd"
import { DatePicker } from "antd-jalali"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaInbox, FaRegSave } from "react-icons/fa"
import { API, Enum, Regex } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"

const { Pages: { TicketManagement: { Company: CompanyLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { BASIC_INFORMATION_PROVINCE_CITIES, TICKET_MANAGEMENT_COMPANY_ID, TICKET_MANAGEMENT_COMPANY_CREATE, TICKET_MANAGEMENT_COMPANY_UPDATE, USER_MANAGEMENT_USER_USERNAME, USER_MANAGEMENT_USER_UPDATE, USER_MANAGEMENT_USER_CREATE } = API
const { Message, Payment } = Enum
const { isNumber, notEnglish } = Regex

interface ProvincesType {
    id: string,
    name: string,
    cities: CityType[],
}

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyFormType {
    name: string,
    tel: string,
    cities: string[],
    user?: string,
    mobile: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
}

export default function CURecord() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const navigate = useNavigate()
    const { accessToken, baseURL, meta } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(true)
    const [provinces, setProvinces] = useState<ProvincesType[]>([])
    const [loadingRecord, setLoadingRecord] = useState<boolean>(false)
    const [user, setUser] = useState<UserType|null>(null)
    const [loadingUser, setLoadingUser] = useState<boolean>(false)
    const { id: companyId } = useParams()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])

    const initialAPI = async () => {
        await getProvinces()
        if (companyId) {
            setLoadingRecord(true);
            await getRecord(companyId)
        }
    }

    const generatePassword = (length = 8): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

        const randomValues = new Uint32Array(length);
        window.crypto.getRandomValues(randomValues);

        return Array.from(randomValues)
            .map(v => chars[v % chars.length])
            .join('');
    }

    const getUser = async (mobile: string) => axios.get(USER_MANAGEMENT_USER_USERNAME + mobile).then(async (response: { data: UserType }) => {
        const { data: user } = response
        form.setFieldsValue({
            user: user?.id,
            firstName: user?.firstName,
            lastName: user?.lastName,
            nationalCode: user?.nationalCode,
        })
        setUser(user);
        setLoadingUser(false)
    }).catch((error: AxiosError) => {
        setUser(null);
        setLoadingUser(false)
    })

    const getProvinces = async () => axios.get(BASIC_INFORMATION_PROVINCE_CITIES).then((response: { data: ProvincesType[] }) => {
        const { data: provinces } = response
        setProvinces(provinces)
        setLoadingProvinces(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const getRecord = async (companyId: string): Promise<CompanyType> => axios.get(TICKET_MANAGEMENT_COMPANY_ID + companyId).then((response: { data: CompanyType }) => {
        const { data: company } = response
        setUser(company.user)
        form.setFieldsValue({ ...company, ...company.user, user: company.user.id, mobile: `0${company.user.mobile}`, cities: company.cities.map(({ id }) => id) })
        setLoadingRecord(false);
        return company;
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const upsertCompany  = async (company: CompanyFormType) => {
        if (companyId) {
            axios.patch(TICKET_MANAGEMENT_COMPANY_UPDATE + companyId, company).then(async (response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                await getRecord(companyId)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            axios.post(TICKET_MANAGEMENT_COMPANY_CREATE, company).then(async (response: { data: { id: string } }) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                const { data: { id } } = response
                navigate('/ticket-management/company/edit/' + id)
                await getRecord(id)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    const upsertUser  = async (company: CompanyFormType): Promise<UserType> => {
        if (company.user) {
            return axios.patch(USER_MANAGEMENT_USER_UPDATE + company.user, company).then(async (response: { data: UserType }) => {
                const { data: user } = response;
                form.setFieldsValue({
                    user: user?.id,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    nationalCode: user?.nationalCode,
                });
                return user
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            return axios.post(USER_MANAGEMENT_USER_CREATE, { ...company, mobile: company.mobile.substring(1), password: generatePassword() }).then(async (response: { data: UserType }) => {
                const { data: user } = response;
                form.setFieldsValue({
                    user: user?.id,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    nationalCode: user?.nationalCode,
                })
                return user;
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    const onFinish = async (company: CompanyFormType) => {
        setLoadingRecord(true);
        const user = await upsertUser(company);
        if (user) await upsertCompany({ ...company, user: user.id });
        else setLoadingRecord(false);
    }

    return (
        <Spin spinning={loadingRecord || loadingProvinces || loadingUser} tip={Share.loading} size="large">
            <Form form={form} name="cu-record" onFinish={onFinish} style={{ maxWidth: 450 }}>
                <Form.Item
                    label={CompanyLanguage.name}
                    name="name"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.name} ${Validation.required}` },
                    ]}
                >
                    <Input minLength={3} />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.position}
                    name="position"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.position} ${Validation.required}` },
                    ]}
                >
                    <InputNumber style={{width: '100%'}} min={1} max={99} />
                </Form.Item>
                <Form.Item name="user" style={{display:'none'}}>
                    <Input type="hidden" />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.mobile}
                    name="mobile"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.mobile} ${Validation.required}` },
                        { pattern: isNumber, message: `${CompanyLanguage.mobile} ${Validation.pattern}` },
                        { len: 11, message: `${CompanyLanguage.mobile} 11 ${Validation.length}` },
                    ]}
                >
                    <Input
                        type="tel"
                        maxLength={11}
                        placeholder={CompanyLanguage.mobile}
                        onBlur={async (event: any) => {
                            const mobile = event?.target?.value;
                            form.resetFields(['user', 'firstName', 'lastName', 'nationalCode'])
                            forceUpdate({})
                            if (mobile.length === 11) {
                                setLoadingUser(true)
                                await getUser(mobile.substring(1))
                            }
                        }}
                    />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.firstName}
                    name="firstName"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.firstName} ${Validation.required}` },
                        { min: 3, message: `${CompanyLanguage.firstName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${CompanyLanguage.firstName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input maxLength={30} placeholder={CompanyLanguage.firstName} />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.lastName}
                    name="lastName"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.lastName} ${Validation.required}` },
                        { min: 3, message: `${CompanyLanguage.lastName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${CompanyLanguage.lastName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input maxLength={30} placeholder={CompanyLanguage.lastName} />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.nationalCode}
                    name="nationalCode"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.nationalCode} ${Validation.required}` },
                        { pattern: isNumber, message: `${CompanyLanguage.nationalCode} ${Validation.pattern}` },
                        { len: 10, message: `${CompanyLanguage.nationalCode} 10 ${Validation.length}` },
                    ]}
                >
                    <Input type="tel" maxLength={10} placeholder={CompanyLanguage.nationalCode} />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.tel}
                    name="tel"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.tel} ${Validation.required}` },
                        { pattern: isNumber, message: `${CompanyLanguage.tel} ${Validation.pattern}` },
                        { min: 3, message: `${CompanyLanguage.tel} ${Validation.min} 3 ${Validation.length}` },
                        { max: 15, message: `${CompanyLanguage.tel} ${Validation.max} 15 ${Validation.length}` },
                    ]}
                >
                    <Input placeholder={CompanyLanguage.tel} />
                </Form.Item>
                <Form.Item
                    label={CompanyLanguage.cities}
                    name="cities"
                    hasFeedback
                    rules={[
                        { required: true, message: `${CompanyLanguage.cities} ${Validation.required}` },
                    ]}
                >
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder={Share.pleaseSelectedRecords}
                        optionFilterProp="name"
                        options={provinces.map(({ name, cities }) => ({ label: name, options: cities.map(({ id, name }) => ({ label: name, value: id, name }))}))}
                    />
                </Form.Item>
            <Form.Item shouldUpdate>
                {() => (
                    <Button
                        icon={<FaRegSave />}
                        type="primary"
                        htmlType="submit"
                        block
                        disabled={
                            !form.getFieldValue('mobile') ||
                            !form.getFieldValue('firstName') ||
                            !form.getFieldValue('lastName') ||
                            !form.getFieldValue('nationalCode') ||
                            !form.getFieldValue('name') ||
                            !form.getFieldValue('position') ||
                            !form.getFieldValue('tel') ||
                            !form.getFieldValue('cities') ||
                            !!form.getFieldsError().filter(({ errors }) => errors.length).length
                    }
                    >
                        { Share.save }
                    </Button>
                )}
            </Form.Item>
        </Form>
        </Spin>
    )
}
